// Haircare product-type detector + matcher.
//
// The hair routine steps (e.g. "Apply leave-in conditioner",
// "Seal with shea butter", "Bond-repair treatment", "Light scalp oil") name
// PRODUCT TYPES rather than ingredients. So unlike skincare matching which
// keys on actives, this one matches step text against product *categories*
// in our products table, then filters by the user's hair_type.

import { supabase } from "./supabase";

// Order matters — first match wins (more specific phrases first).
// `category` matches values in products.category (case-insensitive ilike).
export const HAIR_PRODUCT_TYPES = [
  { label: "Bond Repair", pattern: /\b(bond[-\s]?repair|bond builder|olaplex)\b/i, category: "Treatment" },
  { label: "Deep Conditioner", pattern: /\b(deep conditioner|deep condition|hair mask|protein mask)\b/i, category: "Deep Conditioner" },
  { label: "Leave-in", pattern: /\bleave[-\s]?in\b/i, category: "Leave-in" },
  { label: "Curl Cream", pattern: /\b(curl cream|defining cream|curl definer)\b/i, category: "Curl Cream" },
  { label: "Edge Control", pattern: /\bedge control\b/i, category: "Edge Control" },
  { label: "Scalp Oil", pattern: /\b(scalp oil|loc oil|scalp serum|hot oil)\b/i, category: "Scalp Oil" },
  { label: "Scalp Treatment", pattern: /\b(scalp treatment|scalp scrub|scalp tonic|scalp spray)\b/i, category: "Scalp Treatment" },
  { label: "Hair Butter", pattern: /\b(hair butter|shea butter|seal with butter|heavy butter|styling butter|seal ends)\b/i, category: "Hair Butter" },
  { label: "Hair Oil", pattern: /\b(hair oil|argan oil|jojoba oil|jbco|jamaican black castor|seal with oil|light oil)\b/i, category: "Hair Oil" },
  { label: "Gel", pattern: /\b(scrunch with gel|hair gel|styling gel|flax seed gel)\b/i, category: "Gel" },
  { label: "Mousse", pattern: /\b(mousse|styling mousse)\b/i, category: "Styling Mousse" },
  { label: "Conditioner", pattern: /\b(rinse[-\s]?out conditioner|condition\b|hydrating conditioner)\b/i, category: "Conditioner" },
  { label: "Co-Wash", pattern: /\b(co[-\s]?wash|cleansing conditioner)\b/i, category: "Co-Wash" },
  { label: "Shampoo", pattern: /\b(shampoo|cleanse hair|sulfate[-\s]?free shampoo)\b/i, category: "Shampoo" },
  { label: "Leave-in Spray", pattern: /\b(refresh spray|leave[-\s]?in spray|water \+ leave[-\s]?in|spritz|refresh ends with water)\b/i, category: "Leave-in Spray" },
  { label: "Detangler", pattern: /\b(detangler|finger detangle|wide[-\s]?tooth)\b/i, category: "Detangler" },
  { label: "Heat Protectant", pattern: /\b(uv protectant|heat protectant|thermal spray)\b/i, category: "Treatment Spray" },
  { label: "Pre-poo", pattern: /\bpre[-\s]?poo\b/i, category: "Hair Oil" },
];

export function detectHairProducts(text = "") {
  if (!text) return [];
  const seen = new Set();
  const out = [];
  for (const t of HAIR_PRODUCT_TYPES) {
    if (t.pattern.test(text) && !seen.has(t.label)) {
      seen.add(t.label);
      out.push(t);
    }
  }
  return out;
}

// Maps user-facing hair_type values to the EXACT lowercase tokens stored in
// products.hair_types[]. STRICT: returns only tokens for the SAME family —
// no cross-family fallback. A "Straight 1A-1C" user will ONLY see products
// tagged "straight"; a "Coily 4A-4B" user will ONLY see products tagged
// "coily". The DB has some verbose tokens (e.g. "straight 1a-1c"), so we
// return an array of same-family variants that all pass the strict check.
function hairTokensFor(hairType) {
  if (!hairType) return [];
  const ht = hairType.toLowerCase();
  if (ht.includes("kinky") || ht.includes("4c")) return ["kinky", "4c"];
  if (ht.includes("coily") || ht.includes("4a") || ht.includes("4b"))
    return ["coily", "4a", "4b"];
  if (ht.includes("curly") || ht.includes("3"))
    return ["curly", "3a", "3b", "3c", "3a-3c"];
  if (ht.includes("wavy") || ht.includes("2"))
    return ["wavy", "2a", "2b", "2c", "wavy 2a-2c"];
  if (ht.includes("straight") || ht.includes("1"))
    return ["straight", "1a", "1b", "1c", "straight 1a-1c"];
  if (ht.includes("loc")) return ["locs"];
  if (ht.includes("protective")) return ["protective"];
  if (ht.includes("chemical") || ht.includes("relax") || ht.includes("color"))
    return ["chemically treated", "chemically_treated", "relaxed", "color treated", "colour treated"];
  return [ht];
}

// Module-level cache keyed by `${category}::${hairType}`.
const cache = new Map();

// STRICT match: requires products.hair_types[] to include either one of the
// user's same-family tokens or "all". Returns null if no product matches —
// no generic fallback across hair families.
export async function findHairProduct(category, hairType, concerns = []) {
  const key = `${category.toLowerCase()}::${(hairType || "").toLowerCase()}`;
  if (cache.has(key)) return cache.get(key);

  const tokens = hairTokensFor(hairType);
  if (tokens.length === 0) {
    cache.set(key, null);
    return null;
  }

  // Only match products tagged for THIS hair family (or "all"-safe products).
  const { data: matched } = await supabase
    .from("products")
    .select("name, brand, price_tier, price_usd, category, hair_types, concerns")
    .ilike("category", category)
    .overlaps("hair_types", [...tokens, "all"])
    .limit(5);

  let best = (matched || [])[0];
  if (best && concerns.length) {
    const lowerConcerns = concerns.map((c) => c.toLowerCase());
    const overlapped = (matched || []).find((p) =>
      (p.concerns || []).some((c) => lowerConcerns.includes(c.toLowerCase()))
    );
    if (overlapped) best = overlapped;
  }

  cache.set(key, best || null);
  return best || null;
}
